import React, { useState, useEffect } from 'react';
import { loadStripe, type Stripe } from '@stripe/stripe-js';
import {
  Elements,
  CardElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';
import { useCreditPackages, usePurchaseCredits } from '@/hooks/api/useBilling';
import { ErrorMessage, LoadingSpinner } from '@/components/ui';
import { axiosInstance } from '@/lib/axios';
import type { CreditPackage } from '@/types/api.types';

interface StripePaymentFormInnerProps {
  onSuccess?: () => void;
  onCancel?: () => void;
  className?: string;
}

const CARD_ELEMENT_OPTIONS = {
  style: {
    base: {
      fontSize: '16px',
      color: '#424770',
      '::placeholder': {
        color: '#aab7c4',
      },
    },
    invalid: {
      color: '#9e2146',
    },
  },
};

/**
 * Inner payment form component that uses Stripe hooks
 * Must be wrapped with Elements provider
 */
const StripePaymentFormInner: React.FC<StripePaymentFormInnerProps> = ({
  onSuccess,
  onCancel,
  className = '',
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const { data: packages, isLoading: packagesLoading } = useCreditPackages();
  const { mutateAsync: purchaseCredits } = usePurchaseCredits();

  const [selectedPackage, setSelectedPackage] = useState<CreditPackage | null>(null);
  const [successMessage, setSuccessMessage] = useState<string>('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [cardError, setCardError] = useState<string>('');
  const [formError, setFormError] = useState<string>('');

  const handlePackageSelect = (pkg: CreditPackage) => {
    setSelectedPackage(pkg);
    setFormError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!stripe || !elements) {
      setFormError('Stripe has not loaded yet. Please try again.');
      return;
    }

    if (!selectedPackage) {
      setFormError('Please select a credit package.');
      return;
    }

    const cardElement = elements.getElement(CardElement);
    if (!cardElement) {
      setFormError('Card element not found. Please refresh and try again.');
      return;
    }

    setIsProcessing(true);
    setCardError('');
    setFormError('');

    try {
      // Create payment method with Stripe
      const { error: pmError, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card: cardElement,
      });

      if (pmError) {
        throw new Error(pmError.message || 'Failed to create payment method');
      }

      if (!paymentMethod) {
        throw new Error('Payment method creation failed');
      }

      // Call backend to purchase credits
      const response = await purchaseCredits({
        package_id: selectedPackage.id,
        payment_method_id: paymentMethod.id,
      });

      // Payment successful
      setSuccessMessage(
        `Successfully purchased ${response.credits_added} credits! New balance: ${response.new_credit_balance} credits.`
      );

      // Clear card element
      cardElement.clear();

      setTimeout(() => {
        setSuccessMessage('');
        setIsProcessing(false);
        onSuccess?.();
      }, 2000);
    } catch (error: any) {
      console.error('Payment error:', error);

      const errorMessage =
        error?.response?.data?.detail ||
        error?.message ||
        'Payment failed. Please try again.';

      setFormError(typeof errorMessage === 'object' ? errorMessage.message || 'Payment failed' : errorMessage);
      setIsProcessing(false);
    }
  };

  const handleCardChange = (event: any) => {
    if (event.error) {
      setCardError(event.error.message);
    } else {
      setCardError('');
    }
  };

  return (
    <div className={`bg-white rounded-lg shadow-md p-6 ${className}`}>
      <h2 className="text-xl font-semibold text-gray-900 mb-4">Purchase Credits</h2>

      {successMessage && (
        <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-md">
          <p className="text-sm text-green-800">{successMessage}</p>
        </div>
      )}

      {formError && (
        <ErrorMessage message={formError} className="mb-4" />
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Credit Packages */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-3">Select a Credit Package</label>
          {packagesLoading ? (
            <div className="flex justify-center py-4">
              <LoadingSpinner size="sm" />
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {packages?.map((pkg) => (
                <button
                  key={pkg.id}
                  type="button"
                  onClick={() => handlePackageSelect(pkg)}
                  disabled={isProcessing}
                  className={`
                    relative p-4 border-2 rounded-lg text-left transition-all
                    disabled:opacity-50 disabled:cursor-not-allowed
                    ${
                      selectedPackage?.id === pkg.id
                        ? 'border-indigo-600 bg-indigo-50'
                        : 'border-gray-300 hover:border-indigo-400'
                    }
                  `}
                >
                  {pkg.popular && (
                    <span className="absolute -top-2 right-3 bg-indigo-600 text-white text-xs px-2 py-0.5 rounded-full">
                      Most Popular
                    </span>
                  )}
                  <div className="font-semibold text-gray-900">{pkg.name}</div>
                  <div className="text-2xl font-bold text-gray-900 mt-1">
                    {pkg.credits} credits
                  </div>
                  <div className="text-sm text-gray-600 mt-1">
                    ${pkg.price_usd.toFixed(2)} (${pkg.price_per_credit.toFixed(2)}/credit)
                  </div>
                  {pkg.savings_percent > 0 && (
                    <div className="text-xs text-green-700 font-medium mt-1">
                      Save {pkg.savings_percent}%
                    </div>
                  )}
                  <div className="text-xs text-gray-500 mt-1">{pkg.description}</div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Card Element */}
        <div>
          <label htmlFor="card-element" className="block text-sm font-medium text-gray-700 mb-2">
            Card Information
          </label>
          <div className="p-3 border border-gray-300 rounded-md bg-white">
            <CardElement
              id="card-element"
              options={CARD_ELEMENT_OPTIONS}
              onChange={handleCardChange}
            />
          </div>
          {cardError && <p className="mt-1 text-sm text-red-600">{cardError}</p>}
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3">
          <button
            type="submit"
            disabled={isProcessing || !stripe || !selectedPackage || (!!cardError && cardError.length > 0)}
            className="
              flex-1 px-6 py-3 bg-indigo-600 text-white rounded-md
              hover:bg-indigo-700 transition-colors
              disabled:opacity-50 disabled:cursor-not-allowed
              flex items-center justify-center gap-2
              font-medium
            "
          >
            {isProcessing ? (
              <>
                <LoadingSpinner size="sm" />
                <span>Processing...</span>
              </>
            ) : selectedPackage ? (
              `Purchase ${selectedPackage.credits} Credits - $${selectedPackage.price_usd.toFixed(2)}`
            ) : (
              'Select a Package'
            )}
          </button>

          {onCancel && (
            <button
              type="button"
              onClick={onCancel}
              disabled={isProcessing}
              className="
                px-6 py-3 border border-gray-300 text-gray-700 rounded-md
                hover:bg-gray-50 transition-colors
                disabled:opacity-50 disabled:cursor-not-allowed
              "
            >
              Cancel
            </button>
          )}
        </div>
      </form>

      {/* Info Section */}
      <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-md">
        <h3 className="text-sm font-medium text-blue-900 mb-2">Credit Information</h3>
        <ul className="text-xs text-blue-800 space-y-1">
          <li>1 credit per search (high confidence results)</li>
          <li>0.5 credits for medium confidence results</li>
          <li>Free for low confidence results</li>
          <li>Credits are added instantly after payment</li>
          <li>All transactions are secure and encrypted with Stripe</li>
        </ul>
      </div>
    </div>
  );
};

/**
 * Main Stripe Payment Form component with Elements provider
 */
export const StripePaymentForm: React.FC<StripePaymentFormInnerProps> = (props) => {
  const [stripePromise, setStripePromise] = useState<Promise<Stripe | null> | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string>('');

  useEffect(() => {
    // Fetch Stripe publishable key from backend
    const loadStripeKey = async () => {
      try {
        const response = await axiosInstance.get('/payments/config');
        const { publishable_key } = response.data;

        if (!publishable_key) {
          throw new Error('Stripe publishable key not found');
        }

        setStripePromise(loadStripe(publishable_key));
        setIsLoading(false);
      } catch (err: any) {
        console.error('Failed to load Stripe:', err);
        setError(
          err?.response?.data?.detail ||
            'Payment processing is not available. Please contact support.'
        );
        setIsLoading(false);
      }
    };

    loadStripeKey();
  }, []);

  if (isLoading) {
    return (
      <div className={`bg-white rounded-lg shadow-md p-6 ${props.className || ''}`}>
        <div className="flex items-center justify-center py-12">
          <LoadingSpinner size="lg" />
          <span className="ml-3 text-gray-600">Loading payment form...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`bg-white rounded-lg shadow-md p-6 ${props.className || ''}`}>
        <ErrorMessage message={error} />
      </div>
    );
  }

  if (!stripePromise) {
    return (
      <div className={`bg-white rounded-lg shadow-md p-6 ${props.className || ''}`}>
        <ErrorMessage message="Failed to initialize payment system" />
      </div>
    );
  }

  return (
    <Elements stripe={stripePromise}>
      <StripePaymentFormInner {...props} />
    </Elements>
  );
};
